import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Printer, FileDown, Trash2, Plus, CheckCircle2, XCircle, Upload } from "lucide-react";
import { http, formatSoles, STATUS_OPTIONS, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Panel, Confirm, ItemsEditor, Pill, fmtDateTime, uploadFile } from "./ui";
import { orderPdf } from "./docs";

export function totalsOf(o) {
  const subtotal = (o.items || []).reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const total = Math.max(0, subtotal - (Number(o.discount) || 0) + (Number(o.shipping_cost) || 0));
  const paid = (o.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  return { subtotal, total, paid, balance: total - paid };
}

export function TotalsBlock({ o, set }) {
  const t = totalsOf(o);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Descuento (S/)</Label><Input type="number" step="0.01" value={o.discount ?? 0} onChange={(e) => set("discount", Number(e.target.value))} data-testid="discount-input" /></div>
        <div><Label>Costo de envío (S/)</Label><Input type="number" step="0.01" value={o.shipping_cost ?? 0} onChange={(e) => set("shipping_cost", Number(e.target.value))} data-testid="shipping-input" /></div>
      </div>
      <div className="bg-neutral-50 rounded-xl p-4 space-y-1 text-sm">
        <div className="flex justify-between"><span>Subtotal</span><span>{formatSoles(t.subtotal)}</span></div>
        <div className="flex justify-between"><span>Descuento</span><span>- {formatSoles(o.discount || 0)}</span></div>
        <div className="flex justify-between"><span>Envío</span><span>{formatSoles(o.shipping_cost || 0)}</span></div>
        <div className="flex justify-between font-serif text-xl font-bold pt-2 border-t"><span>Total</span><span className="text-[var(--brand-primary)]" data-testid="detail-total">{formatSoles(t.total)}</span></div>
      </div>
    </div>
  );
}

function Payments({ o, set, advancePct }) {
  const t = totalsOf(o);
  const pays = o.payments || [];
  const upd = (i, k, v) => set("payments", pays.map((p, j) => (j === i ? { ...p, [k]: k === "amount" ? Number(v) : v } : p)));
  const add = (type, amount = 0) => set("payments", [...pays, { date: new Date().toISOString().slice(0, 10), type, method: o.payment_method || "yape", amount: Math.round(amount * 100) / 100, note: "" }]);
  return (
    <div className="space-y-2" data-testid="payments-editor">
      {pays.map((p, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center bg-neutral-50 rounded-xl p-2">
          <Input type="date" className="col-span-6 sm:col-span-3 h-9 text-xs" value={p.date || ""} onChange={(e) => upd(i, "date", e.target.value)} />
          <select className="col-span-6 sm:col-span-2 h-9 rounded-md border border-input px-2 text-xs bg-white" value={p.type} onChange={(e) => upd(i, "type", e.target.value)}>
            <option value="adelanto">Adelanto</option><option value="saldo">Saldo</option><option value="pago">Pago</option>
          </select>
          <select className="col-span-5 sm:col-span-2 h-9 rounded-md border border-input px-2 text-xs bg-white" value={p.method} onChange={(e) => upd(i, "method", e.target.value)}>
            <option value="yape">Yape</option><option value="plin">Plin</option><option value="transferencia">Transferencia</option><option value="efectivo">Efectivo</option><option value="pago_entrega">Contra entrega</option>
          </select>
          <Input type="number" step="0.01" className="col-span-5 sm:col-span-2 h-9 text-sm" value={p.amount} onChange={(e) => upd(i, "amount", e.target.value)} data-testid={`payment-amount-${i}`} />
          <Input className="col-span-10 sm:col-span-2 h-9 text-xs" placeholder="Nota" value={p.note || ""} onChange={(e) => upd(i, "note", e.target.value)} />
          <button onClick={() => set("payments", pays.filter((_, j) => j !== i))} className="col-span-2 sm:col-span-1 flex justify-end text-neutral-400 hover:text-red-600"><Trash2 size={15} /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => add("adelanto", t.total * (advancePct || 50) / 100)} data-testid="add-advance"><Plus size={12} className="mr-1" /> Adelanto {advancePct || 50}%</Button>
        <Button size="sm" variant="outline" onClick={() => add("saldo", Math.max(0, t.balance))} data-testid="add-balance-payment"><Plus size={12} className="mr-1" /> Pago del saldo</Button>
        <Button size="sm" variant="outline" onClick={() => add("pago")} data-testid="add-payment"><Plus size={12} className="mr-1" /> Otro pago</Button>
      </div>
      <div className="flex justify-between text-sm font-semibold pt-2">
        <span>Pagado: <span className="text-emerald-700" data-testid="paid-amount">{formatSoles(t.paid)}</span></span>
        <span>Saldo pendiente: <span className={t.balance > 0 ? "text-amber-700" : "text-emerald-700"} data-testid="balance-amount">{formatSoles(t.balance)}</span></span>
      </div>
    </div>
  );
}

function Receipt({ o, set }) {
  const url = o.receipt_path ? fileUrl(o.receipt_path) : "";
  const isPdf = url.toLowerCase().endsWith(".pdf");
  const st = o.receipt_status || (o.receipt_path ? "pendiente" : null);
  async function up(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    try { set("receipt_path", await uploadFile(f, "receipts")); set("receipt_status", "pendiente"); } catch { toast.error("Error al subir"); }
  }
  return (
    <div data-testid="receipt-panel">
      {url ? (
        <>
          <a href={url} target="_blank" rel="noreferrer">
            {isPdf ? <div className="p-4 bg-neutral-100 rounded-xl text-sm font-semibold">Ver comprobante PDF →</div>
              : <img src={url} alt="Comprobante" className="max-h-72 rounded-xl border mx-auto" data-testid="receipt-image" />}
          </a>
          <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
            <Pill className={st === "verificado" ? "bg-emerald-100 text-emerald-800" : st === "rechazado" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-800"} data-testid="receipt-status">
              {st === "verificado" ? "Verificado" : st === "rechazado" ? "Rechazado" : "Por verificar"}
            </Pill>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => set("receipt_status", "verificado")} data-testid="receipt-verify"><CheckCircle2 size={14} className="mr-1" /> Verificar</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => set("receipt_status", "rechazado")} data-testid="receipt-reject"><XCircle size={14} className="mr-1" /> Rechazar</Button>
            </div>
          </div>
        </>
      ) : <p className="text-sm text-neutral-500">El cliente aún no adjuntó comprobante.</p>}
      <label className="mt-3 inline-flex items-center gap-2 text-xs font-semibold cursor-pointer text-neutral-600 hover:text-[var(--brand-primary)]">
        <Upload size={12} /> {url ? "Reemplazar comprobante" : "Subir comprobante"}
        <input type="file" accept="image/*,.pdf" className="hidden" onChange={up} />
      </label>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useStore();
  const [o, setO] = useState(null);
  const [orig, setOrig] = useState("");
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get(`/orders/${id}`).then((r) => { setO(r.data); setOrig(JSON.stringify(r.data)); }).catch(() => navigate("/admin/pedidos"));
    http.get("/products", { params: { active_only: false } }).then((r) => setProducts(r.data));
  }, [id, navigate]);

  const dirty = useMemo(() => o && JSON.stringify(o) !== orig, [o, orig]);
  if (!o) return <div className="text-neutral-500">Cargando…</div>;
  const set = (k, v) => setO((x) => ({ ...x, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const { data } = await http.patch(`/orders/${o.id}`, o);
      setO(data); setOrig(JSON.stringify(data));
      toast.success("Pedido guardado");
    } catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  }
  async function remove() { await http.delete(`/orders/${o.id}`); toast.success("Pedido eliminado"); navigate("/admin/pedidos"); }

  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin/pedidos" className="text-sm text-neutral-500 flex items-center gap-1 mb-3"><ArrowLeft size={14} /> Pedidos</Link>
      <PageHeader title={`Pedido ${o.order_code}`} subtitle={`Creado ${fmtDateTime(o.created_at)}${o.quote_id ? " · desde cotización" : ""}`}
        actions={<>
          <Button variant="outline" onClick={() => orderPdf(o, settings, "print")} data-testid="order-print"><Printer size={14} className="mr-1" /> Imprimir</Button>
          <Button variant="outline" onClick={() => orderPdf(o, settings)} data-testid="order-pdf"><FileDown size={14} className="mr-1" /> PDF</Button>
          <Confirm onConfirm={remove} title="¿Eliminar este pedido?" trigger={<Button variant="outline" className="text-red-600" data-testid="order-delete"><Trash2 size={14} /></Button>} />
          <Button className="btn-primary" onClick={save} disabled={!dirty || saving} data-testid="order-save"><Save size={14} className="mr-1" /> {saving ? "Guardando…" : dirty ? "Guardar cambios" : "Guardado"}</Button>
        </>} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Panel title="Productos"><ItemsEditor items={o.items || []} onChange={(v) => set("items", v)} products={products} /></Panel>
          <Panel title="Pagos, adelantos y saldos"><Payments o={o} set={set} advancePct={settings?.payment?.advance_percent} /></Panel>
          <Panel title="Observaciones"><Textarea rows={4} value={o.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="order-notes" /></Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Estado">
            <select value={o.status} onChange={(e) => set("status", e.target.value)} className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid="order-status-select">
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Panel>
          <Panel title="Cliente">
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={o.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} data-testid="order-customer-name" /></div>
              <div><Label>Teléfono</Label><Input value={o.customer_phone || ""} onChange={(e) => set("customer_phone", e.target.value)} data-testid="order-customer-phone" /></div>
              <div><Label>Email</Label><Input value={o.customer_email || ""} onChange={(e) => set("customer_email", e.target.value)} /></div>
              <div><Label>Distrito / ciudad</Label><Input value={o.customer_district || ""} onChange={(e) => set("customer_district", e.target.value)} /></div>
              {o.customer_phone && <a className="text-xs font-semibold text-[var(--brand-accent)]" target="_blank" rel="noreferrer" href={`https://wa.me/${o.customer_phone.replace(/\D/g, "")}`}>Escribir por WhatsApp →</a>}
            </div>
          </Panel>
          <Panel title="Totales"><TotalsBlock o={o} set={set} /></Panel>
          <Panel title="Entrega">
            <div className="space-y-3">
              <div><Label>Modalidad</Label><Input value={o.shipping_label || ""} onChange={(e) => set("shipping_label", e.target.value)} placeholder="Ej. Lima Metropolitana" /></div>
              <div><Label>Fecha de entrega</Label><Input type="date" value={o.delivery_date || ""} onChange={(e) => set("delivery_date", e.target.value)} data-testid="order-delivery-date" /></div>
              <div><Label>Indicaciones de entrega</Label><Textarea rows={2} value={o.delivery_notes || ""} onChange={(e) => set("delivery_notes", e.target.value)} /></div>
            </div>
          </Panel>
          <Panel title="Comprobante de pago"><Receipt o={o} set={set} /></Panel>
        </div>
      </div>
    </div>
  );
}
